import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { ModalDialog } from 'components/ModalDialog';
import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import type { ChapterEnum } from 'gql/graphql';
import type { IUseAccess } from 'hooks/useAccess';
import { useProjectData } from 'hooks/useProjectData';
import { useSendGpnAnalyticsEvent } from 'hooks/useSendGpnAnalyticsEvent';
import { Button, IconWarning, Informer } from 'uikit';
import { replacerCost } from 'utils/replacerCost';

import * as R from 'ramda';
import type React from 'react';
import { useMemo, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { gqlAddMtrRow } from '../workOrderQueries';

import { CreateMtrForm } from './CreateMtrForm';

import './CreateMtrDialog.css';

const serializeFormValues = R.evolve({
    workOrderPriceId: Number,
    unitWeight: replacerCost,
    unitCost: replacerCost,
    supplyBy: R.prop('id'),
    mtrType: R.prop('id'),
});

interface ICreateMtrDialog {
    chapter?: ChapterEnum | null;
    accessRules?: IUseAccess['workOrderChangeUer'];
    refreshMtrPriceResponse?: () => void;
}

export const CreateMtrDialog: React.FC<ICreateMtrDialog> = (props) => {
    const { chapter, accessRules, refreshMtrPriceResponse } = props;
    const { id, orderId } = useParams();
    const { sendGpnAnalyticsEvent } = useSendGpnAnalyticsEvent();

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { projectData } = useProjectData(id);

    const [isOpen, setIsOpen] = useState(false);

    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isOpenWarningDialog, setIsOpenWarningDialog] = useState<boolean>(false);
    const [isDirtyFields, setIsDirtyFields] = useState<boolean>(false);
    const [hasErrors, setHasErrors] = useState<boolean>(false);

    const formRef = useRef<HTMLFormElement>(null);

    const [addMtrRowResponse, addMtrRowResponseStatus, , addMtrRow] = useRequest({
        requestId: 'addMtrRowToWorkOrderFromPriceList',
        query: gqlAddMtrRow,
        withEffect: false,
    });

    const analyticsData = useMemo(
        () => ({
            projectId: id,
            orderId: Number(orderId),
            orderStatus: projectData?.contractNavigation?.data?.workOrderNavs?.find((w) => w.id === Number(orderId))
                ?.status,
            projectName: projectData?.name,
            projectType: projectData?.contractType,
            projectDO: projectData?.subsidiary?.name,
            methodology: projectData?.methodology,
        }),
        [
            id,
            orderId,
            projectData?.contractNavigation?.data?.workOrderNavs,
            projectData?.contractType,
            projectData?.methodology,
            projectData?.name,
            projectData?.subsidiary?.name,
        ],
    );

    const handleButtonClick = useCallback(() => {
        setIsOpen(true);

        sendGpnAnalyticsEvent({
            componentId: 'rim_create MTR button_pressed',
            ...analyticsData,
        });
    }, [analyticsData, sendGpnAnalyticsEvent]);

    const handleClickOutside = useCallback(() => {
        if (isDirtyFields) {
            setIsOpenWarningDialog(true);
        } else {
            setIsOpen(false);
            navigate('./', { replace: true });
        }
    }, [isDirtyFields, navigate]);

    const handleModalConfirm = useCallback(() => {
        formRef.current?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    }, []);

    const handleFormSubmit = useCallback(
        (formValues: any) => {
            // TODO
            const input: any = serializeFormValues({
                contractId: projectData?.contractId,
                ...formValues,
            });

            const args = { input, chapter };

            dispatch(addMtrRow(args));
            setIsSubmitted(true);

            sendGpnAnalyticsEvent({
                componentId: 'rim_create MTR create button_pressed',
                ...analyticsData,
            });
        },
        [addMtrRow, analyticsData, chapter, dispatch, projectData?.contractId, sendGpnAnalyticsEvent],
    );

    const handleSubmitWarningDialog = useCallback(() => {
        setIsOpen(false);
        navigate('./', { replace: true });
    }, [navigate]);

    const handleCancelWarningDialog = useCallback(() => {
        setIsOpenWarningDialog(false);
    }, []);

    useEffect(() => {
        if (
            isSubmitted &&
            addMtrRowResponseStatus === RequestStatus.SUCCESS &&
            addMtrRowResponse?.uerAddMtrRow?.result
        ) {
            refreshMtrPriceResponse?.();
            setIsOpen(false);
            navigate('./', {
                replace: true,
                state: { reload: true, selected: addMtrRowResponse?.uerAddMtrRow?.data?.id, chapter },
            });
        }
    }, [
        addMtrRowResponse?.uerAddMtrRow?.data?.id,
        addMtrRowResponse?.uerAddMtrRow?.result,
        addMtrRowResponseStatus,
        chapter,
        isSubmitted,
        navigate,
        refreshMtrPriceResponse,
    ]);

    const isAccessRulesEnabled = Boolean(accessRules);
    const isDisabledCreateMtr = isAccessRulesEnabled && !accessRules?.canManualCreation;

    return (
        <>
            <Button
                disabled={isDisabledCreateMtr}
                label="Создать МТР"
                view="secondary"
                size="s"
                onClick={handleButtonClick}
            />
            <ModalDialog
                title="Создание МТР"
                isOpen={isOpen}
                hasOverlay
                withTitleCloseButton
                onClose={handleClickOutside}
                onConfirm={handleModalConfirm}
                btnConfirmDisabled={hasErrors}
                btnConfirmLabel="Создать"
                btnCancelLabel="Отменить"
                isOnClickOutsideDisable
            >
                <div id="CreateMtrDialog">
                    <Informer status="warning" size="s" icon={IconWarning}>
                        Новая позиция МТР будет добавлена в Прейскурант МТР Шифр для новых позиций будет присвоен после
                        заполнения параметров «Тип» и «Поставка»
                    </Informer>
                    <CreateMtrForm
                        onSubmit={handleFormSubmit}
                        ref={formRef}
                        setIsDirty={setIsDirtyFields}
                        setHasErrors={setHasErrors}
                        chapter={chapter}
                    />
                </div>
            </ModalDialog>
            <ConfirmationDialog
                textHeader="ОТМЕНА СОЗДАНИЯ МТР"
                textConfirmation="Вы уверены, что хотите отменить? Заполненные данные не будут сохранены"
                confirmButtonText="Отменить"
                cancelButtonText="Не отменять"
                onSubmit={handleSubmitWarningDialog}
                isOpened={isOpenWarningDialog}
                onClose={handleCancelWarningDialog}
            />
        </>
    );
};

CreateMtrDialog.displayName = 'CreateMtrDialog';
